export const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.canerozen.com/api'
export const FILES_BASE = API_BASE.replace('/api', '')

export function supportStoreDisplayName(user: any, fallback = 'Mağaza') {
  return (
    user?.store?.name ||
    user?.firm_store?.name ||
    user?.firmStore?.name ||
    user?.active_store?.name ||
    user?.firm?.name ||
    user?.name ||
    fallback
  )
}

export type SupportAttachment = {
  id: number
  name: string
  size: number
  mime: string
  path?: string
  dataUrl?: string
}

export type SupportStep = {
  id?: number | string
  title: string
  done?: boolean
}

export type SupportWatcher = {
  user_id: number
  role: 'developer' | 'manager' | 'support_team' | 'watcher' | string
  name?: string
}

export type SupportItem = {
  id: number
  created_at: number
  status: string
  description: string
  subject?: string
  category?: string
  priority?: string
  attachments: SupportAttachment[]
  assigned_user_name?: string
  assigned_at?: number
  started_at?: number
  resolved_at?: number
  last_message_at?: number
  // Admin-set fields (read-only on firm side)
  admin_subject?: string            // görev adı (admin düzenlerse)
  admin_description?: string         // admin notu
  due_date?: number                  // bitiş tarihi
  start_date?: number                // başlangıç tarihi
  estimated_minutes?: number         // tahmini süre
  tags?: string[]                    // etiketler
  steps?: SupportStep[]              // adımlar (alt görevler)
  admin_response?: string            // admin'in firma için yazdığı açıklama
  platform?: string                  // AI tespit ettiği platform
  interface?: string                 // AI tespit ettiği arayüz
  ai_meta?: {
    summary?: string
    reasoning?: string
    keywords?: string[]
    confidence?: number
    category?: string
    priority?: string
    platform?: string
    interface?: string
    suggested_developer_id?: number | null
    suggested_developer_name?: string | null
    analyzed_at?: number
  } | null
  watchers?: SupportWatcher[]
}

export const PRIORITY_LABELS: Record<string, { label: string; cls: string }> = {
  standard:  { label: 'Standart', cls: 'sp-prio-normal' },
  important: { label: 'Önemli',   cls: 'sp-prio-low' },
  high:      { label: 'Yüksek',   cls: 'sp-prio-high' },
  urgent:    { label: 'Acil',     cls: 'sp-prio-critical' },
  // legacy support
  low:      { label: 'Düşük',   cls: 'sp-prio-low' },
  normal:   { label: 'Normal',  cls: 'sp-prio-normal' },
  critical: { label: 'Kritik',  cls: 'sp-prio-critical' },
}

export function priorityBadge(priority?: string) {
  if (!priority) return null
  return PRIORITY_LABELS[priority] || null
}

export const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  pending_review:       { label: 'Onay Bekliyor', cls: 'sp-status-pending' },
  new:                  { label: 'Yeni', cls: 'sp-status-new' },
  assigned:             { label: 'Dağıtıldı', cls: 'sp-status-assigned' },
  waiting_info:         { label: 'Detaylı Bilgi Talebi', cls: 'sp-status-waiting_info' },
  in_progress:          { label: 'İşlemde', cls: 'sp-status-in_progress' },
  sent_to_test:         { label: 'Teste Verildi', cls: 'sp-status-sent_to_test' },
  returned_from_test:   { label: 'Test Sonucu Geri Döndü', cls: 'sp-status-returned_from_test' },
  test_completed:       { label: 'Test Tamamlandı', cls: 'sp-status-test_completed' },
  informed_customer:    { label: 'Firmaya Bilgi Verildi', cls: 'sp-status-informed_customer' },
}

export function statusBadge(status: string) {
  const s = STATUS_LABELS[status]
  if (!s) return { label: status || '-', cls: 'sp-status-default' }
  return s
}

export function fmtDate(ts?: number | string | null) {
  const n = Number(ts || 0)
  if (!n) return '-'
  try {
    const d = new Date(n)
    if (isNaN(d.getTime())) return '-'
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return '-'
  }
}

export function fmtDateShort(ts?: number | string | null) {
  const n = Number(ts || 0)
  if (!n) return '-'
  try {
    const d = new Date(n)
    if (isNaN(d.getTime())) return '-'
    return d.toLocaleDateString('tr-TR', {
      day: '2-digit', month: 'short', year: 'numeric',
    })
  } catch { return '-' }
}

export function fmtBytes(size?: number) {
  const n = Number(size || 0)
  if (!n) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1)
  const value = n / Math.pow(1024, i)
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${units[i]}`
}

export function durationLabel(start?: number, end?: number) {
  const s = Number(start || 0)
  const e = Number(end || 0)
  if (!s || !e || e <= s) return '-'
  const min = Math.floor((e - s) / 60000)
  const d = Math.floor(min / (60 * 24))
  const h = Math.floor((min % (60 * 24)) / 60)
  const m = min % 60
  if (d > 0) return `${d}g ${h}s ${m}d`
  if (h > 0) return `${h}s ${m}d`
  return `${m}d`
}

/** A ticket is "overdue" if new/assigned and older than 24h */
export function isOverdue(item: SupportItem) {
  const openStatuses = ['new', 'assigned']
  if (!openStatuses.includes(item.status)) return false
  const created = Number(item.created_at || 0)
  if (!created) return false
  return (Date.now() - created) > 24 * 60 * 60 * 1000
}

export function isActive(item: SupportItem) {
  return !['test_completed', 'informed_customer'].includes(item.status)
}

export function authHeaders(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export async function uploadSupportAttachment(file: File): Promise<SupportAttachment> {
  const form = new FormData()
  form.append('file', file)

  const res = await fetch(`${API_BASE}/user/supports/uploads`, {
    method: 'POST',
    headers: authHeaders(),
    body: form,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data?.success || !data?.data) {
    throw new Error(data?.message || 'Dosya yüklenemedi')
  }
  return data.data
}
